import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, RefreshCw, Repeat } from "lucide-react";
import { fmt, sortSources } from "@/lib/finance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { EditableSelect } from "@/components/EditableCell";
import usePermissions from "@/lib/usePermissions";
import { useAuth } from "@/context/AuthContext";

const EMPTY_DRAFT = {
  id: null, name: "", department: "", category: "", sub_category: "",
  source_id: "", amount: "", frequency: "monthly", employee_id: "", is_active: true,
};

/**
 * Finance Automation Engine — Phase 1 (Feb 2026). Founders define fixed
 * monthly costs (rent, subscriptions, salaries) here once; a server-side
 * cron job (00:01 IST on the 1st of every month) drops one unpaid
 * finance_expenses row per active template straight into Pending Payments
 * — no manual re-entry every month. Payroll rows are the SAME templates,
 * just tagged with an Employee (see the "This is a salary" toggle below).
 */
export default function ManageRecurringTab() {
  const { user } = useAuth();
  const can = usePermissions();
  const [rows, setRows] = useState([]);
  const [tree, setTree] = useState([]);
  const [sources, setSources] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [isSalary, setIsSalary] = useState(false);

  const canCreate = can("finance_create");
  const canEdit = can("finance_edit");
  const canDelete = can("finance_delete");

  const load = async () => {
    setLoading(true);
    try {
      const [t, cats, src, users] = await Promise.all([
        api.get("/finance/recurring-templates"),
        api.get("/finance/categories"),
        api.get("/finance/sources"),
        api.get("/users", { params: { kind: "internal" } }),
      ]);
      setRows(t.data || []);
      setTree(cats.data?.tree || []);
      setSources(src.data || []);
      setEmployees(users.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const departments = tree.map((d) => d.department);
  const categoriesFor = (dept) => {
    const scope = dept ? tree.filter((d) => d.department === dept) : tree;
    const set = new Set();
    scope.forEach((d) => (d.categories || []).forEach((c) => set.add(c.category)));
    return [...set].sort();
  };
  const subsFor = (dept, cat) => {
    if (!cat) return [];
    const scope = dept ? tree.filter((d) => d.department === dept) : tree;
    const set = new Set();
    scope.forEach((d) => (d.categories || []).forEach((c) => {
      if (c.category === cat) (c.sub_categories || []).forEach((s) => set.add(s.sub_category));
    }));
    return [...set].sort();
  };

  const openCreate = () => { setDraft(EMPTY_DRAFT); setIsSalary(false); setDialogOpen(true); };
  const openEdit = (r) => {
    setDraft({
      id: r.id, name: r.name, department: r.department || "", category: r.category || "",
      sub_category: r.sub_category || "", source_id: r.source_id || "", amount: r.amount,
      frequency: r.frequency || "monthly", employee_id: r.employee_id || "", is_active: r.is_active,
    });
    setIsSalary(!!r.employee_id);
    setDialogOpen(true);
  };

  const isValid = useMemo(() => {
    if (!draft.name.trim() || !draft.amount || Number(draft.amount) <= 0) return false;
    if (isSalary) return !!draft.employee_id;
    return true;
  }, [draft, isSalary]);

  const save = async () => {
    if (!isValid) return;
    setSaving(true);
    const body = {
      name: draft.name.trim(),
      department: isSalary ? null : (draft.department || null),
      category: isSalary ? null : (draft.category || null),
      sub_category: isSalary ? null : (draft.sub_category || null),
      source_id: draft.source_id || null,
      amount: Number(draft.amount),
      frequency: draft.frequency || "monthly",
      employee_id: isSalary ? draft.employee_id : null,
      is_active: draft.is_active,
    };
    try {
      if (draft.id) await api.patch(`/finance/recurring-templates/${draft.id}`, body);
      else await api.post("/finance/recurring-templates", body);
      toast.success(draft.id ? "Template updated" : "Recurring template created");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/finance/recurring-templates/${deleteId}`);
      toast.success("Template deleted");
      setDeleteId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/finance/recurring-templates/${r.id}`, {
        name: r.name, department: r.department, category: r.category, sub_category: r.sub_category,
        source_id: r.source_id, amount: r.amount, frequency: r.frequency, employee_id: r.employee_id,
        is_active: !r.is_active,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateNow = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/finance/recurring-templates/generate-now");
      toast.success(data.generated > 0
        ? `Generated ${data.generated} expense row${data.generated === 1 ? "" : "s"} for this month`
        : "Already up to date — nothing new to generate for this month");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  const sourceLabel = (sid) => sources.find((s) => s.id === sid)?.name || "—";

  return (
    <div className="p-4 sm:p-8" data-testid="manage-recurring-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-display text-lg text-slate-900 flex items-center gap-2"><Repeat size={16} className="text-[#F54800]" /> Recurring Templates</h2>
          <p className="text-xs text-slate-500 mt-0.5">Fixed monthly costs (rent, subscriptions, salaries) auto-generate an unpaid Expense row on the 1st of every month.</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "founder" && (
            <Button variant="outline" className="rounded-sm h-8 text-xs" onClick={generateNow} disabled={generating} data-testid="recurring-generate-now-btn">
              {generating ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCw size={12} className="mr-1" />} Generate this month now
            </Button>
          )}
          {canCreate && (
            <Button className="rounded-sm h-8 text-xs bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={openCreate} data-testid="recurring-add-btn">
              <Plus size={12} className="mr-1" /> Add template
            </Button>
          )}
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm" data-testid="recurring-templates-table">
          <thead className="bg-slate-50">
            <tr>
              {["Name", "Category", "Employee", "Amount", "Frequency", "Last Generated", "Active", "Actions"].map((h) => (
                <th key={h} className="label-overline px-3 py-2 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-slate-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-slate-400">No recurring templates yet — click <b>Add template</b> above.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`recurring-row-${r.id}`} className={!r.is_active ? "opacity-50" : ""}>
                <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.employee_id ? "—" : [r.department, r.category, r.sub_category].filter(Boolean).join(" › ") || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.employee_name || "—"}</td>
                <td className="px-3 py-2 font-mono text-right">{fmt(r.amount)}</td>
                <td className="px-3 py-2 text-xs capitalize">{r.frequency}</td>
                <td className="px-3 py-2 text-xs text-slate-500" data-testid={`recurring-last-gen-${r.id}`}>{r.last_generated_month || "Never"}</td>
                <td className="px-3 py-2"><Switch checked={r.is_active} onCheckedChange={() => toggleActive(r)} disabled={!canEdit} data-testid={`recurring-toggle-active-${r.id}`} /></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    {canEdit && <button onClick={() => openEdit(r)} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`recurring-edit-${r.id}`}><Pencil size={13} /></button>}
                    {canDelete && <button onClick={() => setDeleteId(r.id)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`recurring-delete-${r.id}`}><Trash2 size={13} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="recurring-template-dialog">
          <DialogHeader><DialogTitle className="font-display">{draft.id ? "Edit" : "Add"} recurring template</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="label-overline">Name *</label>
              <Input className="rounded-sm mt-1" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Office Rent, WeWork Subscription" data-testid="recurring-form-name" />
            </div>

            <div className="flex items-center justify-between rounded-sm border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm text-slate-800">This is a salary (link to an employee)</div>
                <div className="text-[11px] text-slate-400">Pre-generates a monthly payroll row instead of a category-based expense.</div>
              </div>
              <Switch checked={isSalary} onCheckedChange={setIsSalary} data-testid="recurring-form-is-salary" />
            </div>

            {isSalary ? (
              <div>
                <label className="label-overline">Employee *</label>
                <EditableSelect
                  value={draft.employee_id || "none"}
                  onChange={(v) => setDraft({ ...draft, employee_id: v === "none" ? "" : v })}
                  options={[{ value: "none", label: "Select employee…" }, ...employees.map((e) => ({ value: e.id, label: e.name }))]}
                  testId="recurring-form-employee"
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="label-overline">Department</label>
                  <EditableSelect
                    value={draft.department || "none"}
                    onChange={(v) => setDraft({ ...draft, department: v === "none" ? "" : v, category: "", sub_category: "" })}
                    options={[{ value: "none", label: "—" }, ...departments.map((d) => ({ value: d, label: d }))]}
                    testId="recurring-form-department"
                  />
                </div>
                <div>
                  <label className="label-overline">Category</label>
                  <EditableSelect
                    value={draft.category || "none"}
                    onChange={(v) => setDraft({ ...draft, category: v === "none" ? "" : v, sub_category: "" })}
                    options={[{ value: "none", label: "—" }, ...categoriesFor(draft.department || undefined).map((c) => ({ value: c, label: c }))]}
                    testId="recurring-form-category"
                  />
                </div>
                <div>
                  <label className="label-overline">Sub-category</label>
                  <EditableSelect
                    value={draft.sub_category || "none"}
                    onChange={(v) => setDraft({ ...draft, sub_category: v === "none" ? "" : v })}
                    options={[{ value: "none", label: "—" }, ...subsFor(draft.department || undefined, draft.category || undefined).map((s) => ({ value: s, label: s }))]}
                    testId="recurring-form-subcategory"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="label-overline">Amount ₹ *</label>
                <Input type="number" min="0" className="rounded-sm mt-1" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} data-testid="recurring-form-amount" />
              </div>
              <div>
                <label className="label-overline">Frequency</label>
                <EditableSelect value={draft.frequency} onChange={(v) => setDraft({ ...draft, frequency: v })} options={[{ value: "monthly", label: "Monthly" }]} testId="recurring-form-frequency" />
              </div>
            </div>

            <div>
              <label className="label-overline">Source Account</label>
              <EditableSelect
                value={draft.source_id || "none"}
                onChange={(v) => setDraft({ ...draft, source_id: v === "none" ? "" : v })}
                options={[{ value: "none", label: "No specific account" }, ...sortSources(sources).map((s) => ({ value: s.id, label: s.name }))]}
                testId="recurring-form-source"
              />
            </div>

            <div className="flex items-center justify-between rounded-sm border border-slate-200 px-3 py-2">
              <div className="text-sm text-slate-800">Active</div>
              <Switch checked={draft.is_active} onCheckedChange={(v) => setDraft({ ...draft, is_active: v })} data-testid="recurring-form-active" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={!isValid || saving} data-testid="recurring-form-save"
              className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed">
              {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="recurring-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this recurring template?</AlertDialogTitle>
            <AlertDialogDescription>This stops future months from auto-generating. Expense rows already generated are not affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recurring-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="recurring-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
